import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/lib/auth/AuthContext";
import { takePendingInvitation } from "@/pages/family/AcceptInvitationPage";

/**
 * Carries a family invitation across the Google sign-in detour.
 *
 * The link lands on `/invitations/:token` while signed out, and the only way
 * forward is OAuth — which returns to `/` and knows nothing about where the
 * person was going. Without this, an invitation is a link that works only for
 * somebody who already happened to be logged in, which is nobody: the whole
 * point is that it is opened by the *other* parent.
 *
 * Consumed exactly once. A token left in storage would drag the account back to
 * the invitation page on every visit long after it was accepted.
 */
export function usePendingInvitation() {
  const { authenticated, isGuardian, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading || !authenticated || !isGuardian) return;
    // Already there — taking the token now would strip it out from under the
    // page that is about to use it.
    if (location.pathname.startsWith("/invitations/")) return;

    const token = takePendingInvitation();
    if (token) navigate(`/invitations/${token}`, { replace: true });
  }, [authenticated, isGuardian, loading, location.pathname, navigate]);
}
